import { useEffect, useRef } from 'react';
import { MapStore } from './types';

/**
 * Adds a pulsing ring animation overlay on the reference store marker.
 * The overlay is a CSS-animated div positioned via google.maps.OverlayView.
 */
export function usePulseOverlay(
  map: google.maps.Map | null,
  stores: MapStore[],
  referenceStoreId?: string
) {
  const overlayRef = useRef<google.maps.OverlayView | null>(null);

  useEffect(() => {
    if (!map || !referenceStoreId || !window.google) return;

    const store = stores.find((s) => s.id === referenceStoreId);
    if (!store?.latitude || !store?.longitude) return;

    const position = new window.google.maps.LatLng(store.latitude, store.longitude);
    const isCust =
      !!store.customer_id ||
      !!store.purchek_install_date ||
      !!store.containment_install_date ||
      !!store.cart_manager_install_date ||
      !!store.subscription_start_date ||
      !!store.paired_wheel_install_date;

    const hasLabels =
      !!store.purchek_install_date ||
      !!store.containment_install_date ||
      !!store.cart_manager_install_date ||
      !!store.subscription_start_date;

    const circleCenterOffset = hasLabels ? 10 : 0;

    class PulseOverlay extends window.google.maps.OverlayView {
      private div: HTMLDivElement | null = null;

      onAdd() {
        const panes = this.getPanes();
        if (!panes) return;

        this.div = document.createElement('div');
        this.div.style.position = 'absolute';
        this.div.style.pointerEvents = 'none';
        this.div.style.width = '40px';
        this.div.style.height = '40px';
        this.div.style.zIndex = '999';
        this.div.className = isCust
          ? 'reference-marker-pulse'
          : 'reference-marker-pulse red';

        panes.overlayLayer.appendChild(this.div);
      }

      onRemove() {
        if (this.div) {
          this.div.parentNode?.removeChild(this.div);
          this.div = null;
        }
      }

      draw() {
        if (!this.div) return;
        const projection = this.getProjection();
        const pos = projection.fromLatLngToDivPixel(position);
        if (pos) {
          this.div.style.left = pos.x - 20 + 'px';
          this.div.style.top = pos.y - circleCenterOffset - 20 + 'px';
        }
      }
    }

    // Slight delay so the marker renders first
    const timer = setTimeout(() => {
      const overlay = new PulseOverlay();
      overlay.setMap(map);
      overlayRef.current = overlay;
    }, 300);

    return () => {
      clearTimeout(timer);
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
        overlayRef.current = null;
      }
    };
  }, [map, stores, referenceStoreId]);
}
